import { API_CONFIG } from 'config/api';
import { Employee, CompanyProfile } from 'types';

const CUSTOMER_BASE_URL = API_CONFIG.CUSTOMER.BASE_URL;

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface EmployeeResponse {
  id: string;
  employeeId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  position?: string;
  profileImage?: string;
  dateOfBirth?: string;
  kycStatus: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  company?: any;
  walletBalance?: number;
  creditLimit?: number;
  creditUsed?: number;
}

export interface CompanyResponse {
  id: string;
  name: string;
  slug: string;
  domain: string;
  industry: string;
  address: string;
  phone: string;
  website: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserProfileResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  profileImage?: string;
  dateOfBirth?: string;
  isActive: boolean;
  role: string;
  createdAt: string;
  updatedAt: string;
  emergencyContact?: {
    firstName: string;
    lastName: string;
    phone: string;
    relationship: string;
  };
}

export interface SignupCodeResponse {
  id: string;
  code: string;
  maxUses: number;
  usedCount: number;
  isActive: boolean;
  status: 'active' | 'revoked' | 'exhausted' | 'expired';
  expiresAt: string;
  usedAt?: string;
  usedBy?: string;
  createdAt: string;
}

export interface EmergencyContactRequest {
  firstName: string;
  lastName: string;
  phone: string;
  relationship: string;
}

export interface KYCRequest {
  type: 'NIN' | 'BVN';
  number: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
}

export interface KYCResponse {
  status: 'VERIFIED' | 'REJECTED' | 'PENDING';
  message: string;
  user: any;
}

export class CustomerApiService {
  private getAuthHeaders() {
    const token = localStorage.getItem('vpd_auth_token') || sessionStorage.getItem('vpd_auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  async getCurrentEmployer(): Promise<ApiResponse<EmployeeResponse>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}${API_CONFIG.CUSTOMER.ENDPOINTS.EMPLOYER_ME}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get current employer API error:', error);
      throw new Error('Failed to fetch employer data');
    }
  }

  async getEmployees(): Promise<ApiResponse<EmployeeResponse[]>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}${API_CONFIG.CUSTOMER.ENDPOINTS.EMPLOYEES}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get employees API error:', error);
      throw new Error('Failed to fetch employees');
    }
  }

  async getEmployee(employeeId: string): Promise<ApiResponse<EmployeeResponse>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}${API_CONFIG.CUSTOMER.ENDPOINTS.EMPLOYEES}/${employeeId}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get employee API error:', error);
      throw new Error('Failed to fetch employee');
    }
  }

  async createEmployee(employeeData: Partial<EmployeeResponse>): Promise<ApiResponse<EmployeeResponse>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}${API_CONFIG.CUSTOMER.ENDPOINTS.EMPLOYEES}`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(employeeData),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Create employee API error:', error);
      throw new Error('Failed to create employee');
    }
  }

  async updateEmployee(employeeId: string, updates: Partial<EmployeeResponse>): Promise<ApiResponse<EmployeeResponse>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}${API_CONFIG.CUSTOMER.ENDPOINTS.EMPLOYEES}/${employeeId}`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updates),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Update employee API error:', error);
      throw new Error('Failed to update employee');
    }
  }

  async deleteEmployee(employeeId: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}${API_CONFIG.CUSTOMER.ENDPOINTS.EMPLOYEES}/${employeeId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Delete employee API error:', error);
      throw new Error('Failed to delete employee');
    }
  }

  async getCompany(): Promise<ApiResponse<CompanyResponse>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}${API_CONFIG.CUSTOMER.ENDPOINTS.COMPANY_ME}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get company API error:', error);
      throw new Error('Failed to fetch company data');
    }
  }

  async updateCompany(updates: Partial<CompanyResponse>): Promise<ApiResponse<CompanyResponse>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}${API_CONFIG.CUSTOMER.ENDPOINTS.COMPANY_ME}`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updates),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Update company API error:', error);
      throw new Error('Failed to update company');
    }
  }

  // User Profile Management
  async getCurrentUser(): Promise<ApiResponse<UserProfileResponse>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}/user/me`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get current user API error:', error);
      throw new Error('Failed to fetch user profile');
    }
  }

  async updateUserProfile(updates: Partial<UserProfileResponse>): Promise<ApiResponse<UserProfileResponse>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}/user/me`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updates),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Update user profile API error:', error);
      throw new Error('Failed to update user profile');
    }
  }

  // Emergency Contact Management
  async updateEmergencyContact(contactData: EmergencyContactRequest): Promise<ApiResponse<UserProfileResponse>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}/user/emergency`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(contactData),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Update emergency contact API error:', error);
      throw new Error('Failed to update emergency contact');
    }
  }

  // KYC Management
  async submitKYC(kycData: KYCRequest): Promise<ApiResponse<KYCResponse>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}/user/kyc`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(kycData),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Submit KYC API error:', error);
      throw new Error('Failed to submit KYC data');
    }
  }

  // Signup Code Management
  async generateSignupCode(expiresInDays: number = 30, maxUses: number = 10): Promise<ApiResponse<SignupCodeResponse>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}/user/signup-code`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ expiresInDays, maxUses }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Generate signup code API error:', error);
      throw new Error('Failed to generate signup code');
    }
  }

  async revokeSignupCode(codeId: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}/user/signup-code/${codeId}/revoke`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Revoke signup code API error:', error);
      throw new Error('Failed to revoke signup code');
    }
  }

  async deleteSignupCode(codeId: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}/user/signup-code/${codeId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Delete signup code API error:', error);
      throw new Error('Failed to delete signup code');
    }
  }

  async listSignupCodes(): Promise<ApiResponse<SignupCodeResponse[]>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}/user/signup-codes`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get signup codes API error:', error);
      throw new Error('Failed to fetch signup codes');
    }
  }

  async getActiveSignupCodes(): Promise<ApiResponse<SignupCodeResponse[]>> {
    return this.listSignupCodes();
  }

  // Company Profile Management (using the correct endpoint)
  async getCompanyProfile(): Promise<ApiResponse<CompanyResponse>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}/user/company`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get company profile API error:', error);
      throw new Error('Failed to fetch company profile');
    }
  }

  async updateCompanyProfile(updates: Partial<CompanyResponse>): Promise<ApiResponse<CompanyResponse>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}/user/company`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updates),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Update company profile API error:', error);
      throw new Error('Failed to update company profile');
    }
  }

  // Photo Approval Management
  async getPendingPhotoApprovals(): Promise<ApiResponse<any[]>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}/user/pending-approvals`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get pending photo approvals API error:', error);
      throw new Error('Failed to fetch pending photo approvals');
    }
  }

  async approveEmployeePhoto(employeeId: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}/user/employees/${employeeId}/approve-photo`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Approve employee photo API error:', error);
      throw new Error('Failed to approve employee photo');
    }
  }

  async rejectEmployeePhoto(employeeId: string, reason: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}/user/employees/${employeeId}/reject-photo`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ reason }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Reject employee photo API error:', error);
      throw new Error('Failed to reject employee photo');
    }
  }

  // Employee Status Management
  async updateEmployeeStatus(employeeId: string, status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED'): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}/user/employees/${employeeId}/status`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ status }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Update employee status API error:', error);
      throw new Error('Failed to update employee status');
    }
  }

  // Credit Limit Management  
  async updateEmployeeCreditLimit(employeeId: string, limit: number, reason: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}/user/employees/${employeeId}/credit-limit`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ limit, reason }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Update employee credit limit API error:', error);
      throw new Error('Failed to update employee credit limit');
    }
  }

  // Notifications
  async getNotifications(params: { unreadOnly?: boolean; limit?: number; offset?: number; type?: string } = {}): Promise<ApiResponse<{ notifications: any[]; unreadCount: number; hasMore: boolean }>> {
    try {
      const queryParams = new URLSearchParams();
      if (params.unreadOnly) queryParams.append('unreadOnly', 'true');
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());
      if (params.type) queryParams.append('type', params.type);

      const response = await fetch(`${CUSTOMER_BASE_URL}${API_CONFIG.CUSTOMER.ENDPOINTS.NOTIFICATIONS}?${queryParams.toString()}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get notifications API error:', error);
      throw new Error('Failed to fetch notifications');
    }
  }

  async getUnreadCount(): Promise<ApiResponse<{ unreadCount: number }>> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}${API_CONFIG.CUSTOMER.ENDPOINTS.NOTIFICATIONS_UNREAD}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get unread count API error:', error);
      throw new Error('Failed to fetch unread count');
    }
  }

  async markAsRead(id: string): Promise<ApiResponse> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}${API_CONFIG.CUSTOMER.ENDPOINTS.NOTIFICATION_READ(id)}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Mark notification as read API error:', error);
      throw new Error('Failed to mark notification as read');
    }
  }

  async markAllAsRead(): Promise<ApiResponse> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}${(API_CONFIG.CUSTOMER.ENDPOINTS as any).NOTIFICATIONS_READ_ALL}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Mark all notifications as read API error:', error);
      throw new Error('Failed to mark all notifications as read');
    }
  }

  async deleteNotification(id: string): Promise<ApiResponse> {
    try {
      const response = await fetch(`${CUSTOMER_BASE_URL}${API_CONFIG.CUSTOMER.ENDPOINTS.NOTIFICATION_DELETE(id)}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Delete notification API error:', error);
      throw new Error('Failed to delete notification');
    }
  }
}

export const customerApiService = new CustomerApiService();
